import { ReactNode, useEffect } from 'react'

import {
  DeepPartial,
  FieldValues,
  UnpackNestedValue,
  UseFormMethods,
  useForm,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type { ColorScheme } from '@hivewatch/dark'
import { Button, Drawer } from '@hivewatch/dark'

// The drawer sits on the Chakra v1 modal tier (1400), above the app banners
// (flex items at zIndex 'banner', 1200), matching the incidents filter drawer.
const DEFAULT_Z_INDEX = 1400
const DEFAULT_WIDTH = '400px'

interface FilterDrawerIProps<T extends FieldValues> {
  isOpen: boolean
  onClose: () => void
  colorScheme: ColorScheme
  /** Header title (e.g. "Filter Rules"). */
  title: string
  /**
   * Applied-filter values used to seed the form. The drawer stays mounted while
   * `isOpen` toggles, so these are re-applied on every open — discarding edits
   * that were made but never applied (e.g. closed via Back).
   */
  defaultValues: UnpackNestedValue<DeepPartial<T>>
  /**
   * Field names cleared (set to `null`) by the Clear All button. Restricted to
   * string keys because that is exactly RHF v6's `FieldName<T>` for a flat form
   * (it excludes the `number | symbol` half of `keyof T`), which lets Clear All
   * call `setValue` without a cast.
   */
  fieldNames: Array<Extract<keyof T, string>>
  /** Called with the form values when Apply is pressed. Dispatch + track here;
   * the drawer closes itself afterwards. */
  onApply: (values: UnpackNestedValue<T>) => void
  /** Optional side effect when Clear All is pressed — analytics, and/or custom
   * resets via the form `methods` (e.g. a field whose cleared state isn't
   * `null`). Runs after the `fieldNames` have been nulled. */
  onClear?: (methods: UseFormMethods<T>) => void
  /** The filter rows. Receives the shared form instance so rows can register
   * their `Controller`s against it. */
  children: (methods: UseFormMethods<T>) => ReactNode
  headerActions?: ReactNode
  /** Defaults to `onClose` — the header back arrow closes a single-view drawer. */
  onBack?: () => void
  width?: string
  zIndex?: number
  clearLabel?: string
  applyLabel?: string
}

/**
 * Shared shell for the dark page filter drawers (Rules, Devices, Guard Tours,
 * SOPs, …), styled after `DarkIncidentsFilterDrawer`. Owns the form instance,
 * the Clear All / Apply Filters footer, and the reset-to-applied-on-open
 * behaviour, so each page drawer only supplies its title, default values,
 * filter rows, and apply/clear logic.
 */
export const FilterDrawer = <T extends FieldValues>({
  isOpen,
  onClose,
  colorScheme,
  title,
  defaultValues,
  fieldNames,
  onApply,
  onClear,
  children,
  headerActions,
  onBack,
  width = DEFAULT_WIDTH,
  zIndex = DEFAULT_Z_INDEX,
  clearLabel,
  applyLabel,
}: FilterDrawerIProps<T>) => {
  const { t } = useTranslation()

  const methods = useForm<T>({
    defaultValues,
    reValidateMode: 'onChange',
  })
  const { reset, handleSubmit, setValue } = methods

  useEffect(() => {
    if (isOpen) {
      reset(defaultValues)
    }
  }, [isOpen])

  const applyFilters = handleSubmit((values) => {
    onApply(values)
    onClose()
  })

  const clearAll = () => {
    fieldNames.forEach((name) => setValue(name, null))
    onClear?.(methods)
  }

  return (
    <Drawer
      colorScheme={colorScheme}
      footer={
        <>
          <Button
            colorScheme={colorScheme}
            onClick={clearAll}
            variant='secondary'
          >
            {clearLabel ?? t('common:clear_all')}
          </Button>
          <Button
            colorScheme={colorScheme}
            onClick={() => {
              void applyFilters()
            }}
            variant='primary'
          >
            {applyLabel ?? t('common:apply_filters')}
          </Button>
        </>
      }
      headerActions={headerActions}
      isOpen={isOpen}
      onBack={onBack ?? onClose}
      onClose={onClose}
      title={title}
      width={width}
      zIndex={zIndex}
    >
      {children(methods)}
    </Drawer>
  )
}
